import { useCallback, useMemo } from 'react';
import type { SignalItem } from '../api/types';
import type { MarketTrade } from '../api/useMarketTrades';

/** How far a signal may sit from a trade to still count as the same event. */
const FUZZY_WINDOW_MS = 5 * 60 * 1000;

/**
 * Matches signals to trades, indexed up front so each row costs a lookup, not a
 * scan. Exact `transaction_id` wins; otherwise same address and market within
 * FUZZY_WINDOW_MS, since not every signal carries its trade's id.
 */
export function useTradeSignalMatch(
    signals: SignalItem[] | undefined
): (trade: MarketTrade) => SignalItem[] | undefined {
    const index = useMemo(() => {
        if (!signals || signals.length === 0) return null;
        const byTxId = new Map<string, SignalItem[]>();
        const byAddrMarket = new Map<
            string,
            { signal: SignalItem; time: number }[]
        >();
        for (const s of signals) {
            const txId = (s.fact as Record<string, unknown>)?.transaction_id as
                | string
                | undefined;
            if (txId) {
                const arr = byTxId.get(txId) ?? [];
                arr.push(s);
                byTxId.set(txId, arr);
            }
            if (s.address) {
                const key = `${s.address}:${s.market_id}`;
                const arr = byAddrMarket.get(key) ?? [];
                arr.push({ signal: s, time: new Date(s.opened_at).getTime() });
                byAddrMarket.set(key, arr);
            }
        }
        return { byTxId, byAddrMarket };
    }, [signals]);

    return useCallback(
        (trade: MarketTrade): SignalItem[] | undefined => {
            if (!index) return undefined;

            const exact = index.byTxId.get(trade.id);
            if (exact && exact.length > 0) return exact;

            const candidates = index.byAddrMarket.get(
                `${trade.account}:${trade.marketId}`
            );
            if (!candidates) return undefined;

            const tradeTime = trade.timestamp.getTime();
            const fuzzy = candidates
                .filter(c => Math.abs(tradeTime - c.time) <= FUZZY_WINDOW_MS)
                .map(c => c.signal);
            return fuzzy.length > 0 ? fuzzy : undefined;
        },
        [index]
    );
}
